import React, { useEffect } from 'react';
import { SideNavBar } from '@/components/layouts/SideNavBar';
import { styled, useTheme } from '@mui/material/styles';
import { backgroundColour } from '@/app/theme';
import {
	AuthenticatedTemplate,
	UnauthenticatedTemplate,
	useIsAuthenticated,
	useMsal,
} from '@azure/msal-react';
import { TopNavigationMenu } from '@/components/layouts/TopNavigationMenu';
import { useSelector } from 'react-redux';
import { loginRequest, LOGIN_TYPE } from '@/app/authConfig';
import { Box, CircularProgress, Link, Typography } from '@mui/material';
import {
	useCreateUserFirebaseTokenMutation,
	useGetUserDetailsQuery,
} from '@/features/user/userApi';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useLocation, useNavigate } from 'react-router-dom';
import Toast from '@/components/layouts/Toast';
import UnderMaintenancePage from '@/pages/UnderMaintenancePage';
import { useClientStorageHooks } from '@/hooks/useClientStorageHooks';
import { useValidateStoredFilters } from '@/hooks/useValidateStoredFilters';
import { useMsGraph } from '@/hooks/useMsGraph';
import { AddTaskDialog } from '../dialogs/AddTaskDialog';
import { AddNoteDialog } from '../dialogs/AddNoteDialog';
import { FolderCreationContextProvider } from '@/context/folderCreationContext';
import { AddEmailDrawer } from '@/components/Drawers/AddEmailDrawer';
import { AddUnlayerEmailDrawer } from '@/components/Drawers/AddUnlayerEmailDrawer';
import { CreateComplianceStepDialog } from '../dialogs/CreateComplianceStepDialog';
import { LossReasonDialog } from '../dialogs/LossReasonDialog';
import { PipelineContextProvider } from '@/context/pipelineContext';
import { RenameFileDialog } from '../dialogs/RenameFileDialog';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { useHotjarUser } from '@/hooks/useHotjarUser';
import NotificationChecker from './NotificationChecker';
import FolioUpdateBanner from './FolioUpdateBanner';
import { ClientDrawer } from '@/components/Drawers/ClientDrawer';
import { RelationshipContactDrawer } from '@/components/Drawers/RelationshipContactDrawer';
import { RelationshipCompanyDrawer } from '@/components/Drawers/RelationshipCompanyDrawer';
import ViewContactDrawer from '@/components/Drawers/ViewContactDrawer';
import { useGetFormConfigQuery } from '@/features/config/configApi';
import { useState } from 'react';
import { useMsalAuthState } from '@/utils/msAuth';
import { IS_DEV, IS_PRE } from '@/utils/environment';
import AskAiDrawer from '@/components/Drawers/AskAiDrawer/index.jsx';
import UserProfileDrawer from '@/components/Drawers/UserProfileDrawer';
import BrokerageProfileDrawer from '@/components/Drawers/BrokerageProfileDrawer';
import AutomationConfigDrawer from '@/components/Drawers/AutomationConfigDrawer';
import { IssueReportingDialog } from '@/components/dialogs/IssueReportingDialog';
import { RecordInformedConsentDialog } from '@/components/dialogs/RecordInformedConsentDialog';
import { InformedConsentHistoryDialog } from '@/components/dialogs/InformedConsentHistoryDialog';
import { ObjectContextProvider } from '@/context/ObjectContext';
import { useAdminModeContext } from '@/context/adminModeContext';
import { ADMIN_MODE_BANNER_HEIGHT, AdminModeBanner } from './AdminModeBanner';

/**
 * Renders the navbar component with a sign in or sign out button depending on whether or not a user is authenticated
 * @param props
 */

const LayoutRootWithState = ({ children }) => {
	const { width } = useSelector((s) => s.sideNavSlice);
	const { isAdminMode } = useAdminModeContext();

	return (
		<LayoutRoot navWidth={width} sx={{ pt: isAdminMode ? `${ADMIN_MODE_BANNER_HEIGHT}px` : 0 }}>
			{isAdminMode && <AdminModeBanner />}
			{children}
		</LayoutRoot>
	);
};

const LayoutRoot = styled('div')(({ theme, navWidth }) => {
	return {
		display: 'flex',
		flexDirection: 'column',
		minHeight: '100vh',
		[theme.breakpoints.up('sm')]: {
			paddingLeft: navWidth,
		},
		transition: theme.transitions.create(['padding-left', 'width'], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.enteringScreen,
		}),
	};
});

const LayoutContainer = styled('div')(({ theme }) => ({
	display: 'flex',
	flex: '1 1 auto',
	flexDirection: 'column',
	width: '100%',
	height: '100%',
	backgroundColor:
		theme.palette.mode === 'dark' ? theme.palette.background.default : backgroundColour,
}));

const MainContent = styled('main')({
	flex: '1 0 auto', // Allow this to grow and shrink as needed, but not to base size
	width: '100%', // Fill the width
});

const Footer = styled('footer')(({ theme }) => ({
	flexShrink: '0',
	minHeight: '4em',
	marginTop: '2em',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	borderTop: `1px solid ${theme.palette.divider}`,
}));

const AuthenticatedWrapper = ({ children }) => {
	const isAuthenticated = useIsAuthenticated();

	const { instance } = useMsal();
	const { account, expiration, accessToken } = useSelector((state) => state.msalAccount);
	const firebaseReady = useSelector((state) => state.firebase.firebaseReady);
	const signInError = useSelector((state) => state.firebase.signInError);
	const navigate = useNavigate();
	const location = useLocation();

	const email = account?.username;
	const userQuery = useGetUserDetailsQuery({ email }, {
		skip: !email || !firebaseReady,
	});

	// MSAL auth state check + token refresh
	useMsalAuthState(account);
	// Prime client config fields so create client form loads quickly
	const fieldsQuery = useGetFormConfigQuery({ configType: 'client' }, { skip: !userQuery.data });

	// useHotjarUser();

	// Drop saved filter values (owners, broker groups, stages) that no longer exist
	useValidateStoredFilters();

	const { getGraphAccessToken } = useMsGraph();
	const { updateUserGraphProperties } = useClientStorageHooks(userQuery.data?.brokerGroupId);

	// Update user graph properties when user data is available
	useEffect(() => {
		updateUserGraphProperties();
	}, [userQuery.data]);

	// Graph token fetch — retry when Redux account syncs after MSAL (first boot race).
	// getGraphAccessToken is intentionally omitted from deps (not referentially stable).
	useEffect(() => {
		if (!isAuthenticated || accessToken || !account?.homeAccountId) return;
		getGraphAccessToken().catch((e) => console.log('Error getting access token', e));
	}, [isAuthenticated, accessToken, account?.homeAccountId]);

	// Token expiry check
	useEffect(() => {
		if (!isAuthenticated) return;

		const checkTokenExpiry = async () => {
			const currentTime = new Date().getTime();
			if (!expiration || expiration - currentTime <= 2 * 60000) {
				try {
					await getGraphAccessToken();
				} catch (error) {
					console.error('Error refreshing MS Graph access token', error);
				}
			}
		};
		const intervalId = setInterval(checkTokenExpiry, 30000);
		return () => clearInterval(intervalId);
	}, [expiration, isAuthenticated]);

	// Sign-in was refused, e.g. an admin turned this user off
	if (signInError) {
		return (
			<Box
				sx={{
					backgroundColor: (theme) =>
						theme.palette.mode === 'dark' ? 'neutral.800' : 'neutral.100',
					p: 3,
				}}
			>
				<Typography textAlign='center'>{signInError}</Typography>
			</Box>
		);
	}

	return userQuery.isLoading || userQuery.isUninitialized ? (
		<div
			id='page-layout-loader'
			style={{ display: 'flex', justifyContent: 'center', paddingTop: '4em' }}
		>
			<CircularProgress />
		</div>
	) : userQuery.isError ? (
		<Box
			sx={{
				backgroundColor: (theme) =>
					theme.palette.mode === 'dark' ? 'neutral.800' : 'neutral.100',
				p: 3,
			}}
		>
			<Typography textAlign='center'>
				There was an issue loading your profile. Please contact Folio support for access.
			</Typography>
		</Box>
	) : (
		<LayoutContainer className='page-layout-container'>
			<NotificationChecker />
			{children}
		</LayoutContainer>
	);
};

const today = new Date().getFullYear();

export const PageLayout = (props) => {
	const theme = useTheme();
	const location = useLocation();

	const { account } = useSelector((state) => state.msalAccount);
	const firebaseReady = useSelector((state) => state.firebase.firebaseReady);

	const email = account?.username;

	const { getFeatureFlags, isLoading, isUninitialized, isError } = useFeatureFlagContext();

	const userQuery = useGetUserDetailsQuery({ email }, { skip: !email || !firebaseReady });

	const siteActive = Boolean(getFeatureFlags('siteActive')?.active);

	return (
		<>
			<Toast />
			{isLoading || isUninitialized || (email && userQuery.isLoading) ? (
				<Box marginTop='1em' width='100vw' display='flex' justifyContent={'center'}>
					<CircularProgress />
				</Box>
			) : isError || siteActive ? (
				<>
					{(IS_DEV || IS_PRE) && (
						<Box
							id='dev-banner'
							sx={{
								position: 'fixed',
								top: 4,
								right: -50,
								zIndex: 999999,
								width: 130,
								py: 0.25,
								textAlign: 'center',
								transform: 'rotate(45deg)',
								transformOrigin: 'center',
								backgroundColor: IS_DEV
									? theme.palette.primary.main
									: theme.palette.secondary.main,
								color: 'white',
								fontWeight: 600,
								fontSize: '0.75rem',
								letterSpacing: 1,
								boxShadow: 2,
								pointerEvents: 'none',
							}}
						>
							{IS_DEV ? 'DEV' : 'PRE'}
						</Box>
					)}
					<AuthenticatedTemplate>
						<AuthenticatedWrapper>
							<PipelineContextProvider>
								<FolderCreationContextProvider>
									<ObjectContextProvider>
										<LayoutRootWithState className='page-layout-root'>
											<SideNavBar />
											<MainContent>
												<TopNavigationMenu />
												<FolioUpdateBanner />
												{props.children}
											</MainContent>
											<Footer>
												<Typography
													variant='client_subheader'
													color={theme.palette.text.secondary}
												>
													{(IS_DEV || IS_PRE) && (
														<span>{`[${IS_DEV ? 'DEV' : 'PRE'}] `}</span>
													)}
													{'Folio.insure '}
													{today}
													<span style={{ padding: '0 1em' }}>{'|'}</span>
													<span>
														<Link
															href={'mailto:support@folio.insure'}
															sx={{
																color: theme.palette.text.secondary,
															}}
															target='_blank'
															rel='noopener noreferrer'
														>
															support@folio.insure
														</Link>
													</span>
												</Typography>
											</Footer>
										</LayoutRootWithState>
										{/* Add dialogs below here */}
										<AskAiDrawer />
										<UserProfileDrawer />
										<BrokerageProfileDrawer />
										<AutomationConfigDrawer />
										<AddTaskDialog />
										<AddNoteDialog />
										<AddUnlayerEmailDrawer />
										<AddEmailDrawer />
										<ClientDrawer />
										<RelationshipContactDrawer />
										<RelationshipCompanyDrawer />
										<ViewContactDrawer />
										<CreateComplianceStepDialog />
										<LossReasonDialog />
										<RenameFileDialog />
										<IssueReportingDialog />
										<RecordInformedConsentDialog />
										<InformedConsentHistoryDialog />
									</ObjectContextProvider>
								</FolderCreationContextProvider>
							</PipelineContextProvider>
						</AuthenticatedWrapper>
					</AuthenticatedTemplate>
					<UnauthenticatedTemplate>{props.children}</UnauthenticatedTemplate>
				</>
			) : (
				<UnderMaintenancePage />
			)}
		</>
	);
};
